/**
 * Pipeline chip switcher view (SE-5489, desktop) — the recruitment-pipeline
 * sibling of the Scouting `WatchlistPillsRowView`.
 *
 * Pure presentation for the horizontal chip row that replaces the legacy
 * board-tabs strip in the pipeline top panel. Renders:
 *
 *   [ Recent 1 ] [ Recent 2 ] [ Recent 3 ] [ Active (appended, highlighted) ]  + New   See all pipelines ->
 *
 * Chip ordering is the Container's responsibility — this view maps the array
 * into Pressables and looks up each chip's pre-bound handler. Active/inactive
 * chrome mirrors the watchlist pills row: accent-primary tint + border when the
 * chip is the active pipeline, neutral fill otherwise. When there are no chips
 * to show (zero or one pipeline) it renders a clean minimal state with just the
 * create + "See all pipelines ->" triggers — no chip clutter, no broken layout.
 *
 * SE-6537: the "+ New" trigger restores the pipeline-creation entry point that
 * SE-5489 dropped when this row replaced the legacy BoardTabs strip (which
 * carried a "+ New" tab wired to the still-working `usePipelineCreationFlow`).
 * It renders in BOTH the populated and the empty branch — a user with zero
 * pipelines is exactly the user who needs to create one — and is omitted
 * entirely when no callback is supplied, so an unmigrated caller never renders
 * a dead button.
 * @module features/player-kanban/components/molecules/PipelineChipsRow/PipelineChipsRowView
 */

import { ArrowRightIcon, PlusIcon } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PIPELINE_CHIPS_ROW } from "e2e/testId";

/** A single chip descriptor passed in from the Container. */
export interface PipelineChipItem {
  /** Board id — chip key + selection target. */
  readonly id: string;
  /** Display label (board name). */
  readonly name: string;
}

/** Props for `PipelineChipsRowView`. */
export interface PipelineChipsRowViewProps {
  /** Chips in display order: recents first, then the appended active chip. */
  readonly chips: readonly PipelineChipItem[];
  /** Currently active board id — the chip whose id matches renders highlighted. */
  readonly selectedBoardId: string | null;
  /** True when there are no chips to render (zero/one pipeline) — minimal state. */
  readonly isEmpty: boolean;
  /** Pre-built per-chip tap handlers keyed by board id (stable references). */
  readonly chipHandlers: Readonly<Record<string, () => void>>;
  /** Tap on "See all pipelines ->" — opens the all-pipelines drawer. */
  readonly onOpenSeeAllDrawer: () => void;
  /**
   * SE-6537: tap on "+ New" — opens the pipeline-creation flow. Optional: when
   * omitted the trigger is not rendered at all, so a DIRECT consumer of this
   * component that has not been migrated shows no button rather than a dead
   * one. Note this guarantee stops at the component boundary: `TopPanelView`'s
   * own `onCreateBoard` is required and `TopPanelContainer` always supplies a
   * defined callback, so mounting the row through `TopPanel` always renders
   * the trigger regardless of what the screen passes down.
   */
  readonly onCreatePipeline?: () => void;
  /**
   * SE-6597: whether pipelines exist that the switcher could not load (the
   * board-list page walk stopped while the connection still reported more
   * pages). When true the row states that its list is a lower bound, rather
   * than presenting a short set as everything the user has.
   *
   * Optional and defaulting to false so an unmigrated DIRECT consumer renders
   * the row exactly as before — the same shape `onCreatePipeline` uses above.
   */
  readonly hasUnreachableBoards?: boolean;
}

/**
 * Tailwind class strings for active vs inactive chip chrome. Kept module-level
 * so the JSX stays arrow-shorthand.
 *
 * SE-6554: the active chip follows the design's INFO family, not the accent
 * (CTA) blue it previously borrowed from the watchlist pills row. In the design
 * frame the selected chip is a near-white info surface behind an info border
 * with an info label — a quiet "you are here" marker — where the accent tint it
 * used before read as a filled call-to-action competing with the real buttons
 * in the toolbar above. Both families are blue, which is why the difference
 * survived earlier review; side by side the accent is visibly deeper and more
 * saturated.
 *
 * The border is one step lighter than the label in the design frame. The
 * semantic tier exposes a single info step, so border and label share it here:
 * at a 1px stroke the two are indistinguishable, and inventing a token for the
 * lighter step belongs in the design library, not in a feature molecule.
 */
const ACTIVE_CHIP_CLASS =
  "h-10 max-w-48 flex-row items-center justify-center overflow-hidden rounded-full border border-status-info bg-status-info-surface px-3";
const INACTIVE_CHIP_CLASS =
  "web:hover:bg-surface-subtle h-10 max-w-48 flex-row items-center justify-center overflow-hidden rounded-full bg-surface-raised px-3";
const ACTIVE_CHIP_TEXT = "web:truncate min-w-0 text-status-info";
const INACTIVE_CHIP_TEXT = "web:truncate min-w-0 text-content-secondary";

/**
 * Renders the "See all pipelines ->" trigger. A render helper (not a component)
 * so both the populated and minimal-empty branches emit an identical trigger
 * without a second component in this file.
 * @param onPress - Opens the all-pipelines drawer.
 * @returns The trigger element.
 */
const renderSeeAllTrigger = (onPress: () => void) => (
  <Pressable
    testID={TEST_ID_PIPELINE_CHIPS_ROW.SEE_ALL_BTN}
    accessibilityRole="link"
    accessibilityLabel="See all lists"
    onPress={onPress}
    className="web:hover:opacity-80 h-10 flex-row items-center justify-center gap-2 px-3"
  >
    {/* SE-6488: copy comes from the design frame (node 80:15012), which labels
        this trigger "See all lists". The trailing arrow was already present.
        SE-6554: that node IS Code-Connect-mapped to the Button atom, and this
        stays hand-rolled anyway — deliberately, with the pixels as evidence.
        The design draws this label at 12px MEDIUM; routed through Button the
        same text comes out semibold, because the ratified text-variant set has
        no 12px-medium style and the atom exposes no label weight of its own,
        so its label can only be sized, not lightened. Side by side the mapped
        version reads visibly heavier than the frame. Binding this node needs a
        caption-medium label option on the atom first — see the SE-6590 notes;
        until then the mapping would cost fidelity rather than buy it. */}
    <Text
      variant="unstyled"
      className="font-medium text-caption text-content-muted"
    >
      See all lists
    </Text>
    <Icon as={ArrowRightIcon} size="xs" className="text-content-muted" />
  </Pressable>
);

/**
 * Renders the SE-6537 "+ New" pipeline-creation trigger. A render helper (not a
 * component), matching {@link renderSeeAllTrigger}, so the populated and empty
 * branches emit an identical trigger without a second component in this file.
 *
 * Chrome deliberately echoes the legacy BoardTabs "+ New" tab: a dashed outline
 * that reads as "add something here" rather than as a solid, filled action, so
 * it never competes with the active pipeline for emphasis. It otherwise adopts
 * the height and fully-pilled silhouette of the chips beside it, so it sits
 * inside the row rather than on top of it.
 *
 * SE-6554: the design frame for this row has no creation entry point at all, so
 * there is no design answer for how this should look — it tracks the chips'
 * height so the row keeps one silhouette, and keeps the dashed outline that
 * distinguishes "create" from "select". Removing it is not an option: it is the
 * only pipeline-creation entry point in the product (see SE-6537).
 *
 * Styling intent is described here in prose on purpose: the design-system
 * budget ratchet matches some utility names as bare substrings anywhere in a
 * file, so naming them literally in a comment can inflate a counter with no
 * real violation behind it. The class list on the element below is the source
 * of truth for the actual values.
 * @param onPress - Opens the pipeline-creation flow.
 * @returns The trigger element.
 */
const renderCreateTrigger = (onPress: () => void) => (
  <Pressable
    testID={TEST_ID_PIPELINE_CHIPS_ROW.CREATE_BTN}
    accessibilityRole="button"
    accessibilityLabel="Create new pipeline"
    onPress={onPress}
    className="web:hover:bg-surface-subtle h-10 flex-row items-center justify-center gap-1 rounded-full border border-dashed border-outline-subtle px-3"
  >
    <Icon as={PlusIcon} size="xs" className="text-content-muted" />
    <Text
      variant="unstyled"
      className="font-medium text-caption text-content-muted"
    >
      New
    </Text>
  </Pressable>
);

/**
 * SE-6597: renders the truncation affordance — the visible counterpart of the
 * page-cap message that used to exist only as a console warning.
 *
 * Deliberately the same KIND of affordance SE-6575 gave `BoardTabs`: muted
 * caption copy sitting inline in the strip, stating that the list on screen is
 * a lower bound. That surface annotates its board count (`N+ boards (more not
 * loaded)`); this row shows recents rather than a count, so there is no honest
 * number to qualify and the copy carries the same fact without inventing one.
 * Two surfaces, one vocabulary — not a second pattern.
 *
 * A render helper (not a component), matching the two triggers above, so the
 * populated and empty branches emit an identical element.
 * @returns The affordance element.
 */
const renderTruncationIndicator = () => (
  <Text
    variant="unstyled"
    testID={TEST_ID_PIPELINE_CHIPS_ROW.TRUNCATION_INDICATOR}
    className="font-medium text-caption text-content-muted"
  >
    More lists not loaded
  </Text>
);

/**
 * Renders the pipeline chip row.
 * @param root0 - View props
 * @param root0.chips - Chips in display order (recents then appended active)
 * @param root0.selectedBoardId - Active board id (drives highlight)
 * @param root0.isEmpty - Whether to render the minimal empty state
 * @param root0.chipHandlers - Per-chip stable tap handlers keyed by id
 * @param root0.onOpenSeeAllDrawer - "See all pipelines" tap handler
 * @param root0.onCreatePipeline - "+ New" tap handler (SE-6537); omitted = no trigger
 * @param root0.hasUnreachableBoards - Whether pipelines exist beyond those loaded (SE-6597)
 * @returns The chip row, ready to mount in the top panel center region.
 */
const PipelineChipsRowView = ({
  chips,
  selectedBoardId,
  isEmpty,
  chipHandlers,
  onOpenSeeAllDrawer,
  onCreatePipeline,
  hasUnreachableBoards = false,
}: PipelineChipsRowViewProps) => (
  <Box testID={TEST_ID_PIPELINE_CHIPS_ROW.ROOT}>
    {isEmpty ? (
      <Stack
        direction="horizontal"
        testID={TEST_ID_PIPELINE_CHIPS_ROW.EMPTY_STATE}
        className="h-10 items-center gap-2"
      >
        {onCreatePipeline ? renderCreateTrigger(onCreatePipeline) : null}
        {/* SE-6597: the affordance must survive into the EMPTY branch. Under
            SE-6577's newest-first ordering the loaded window holds the most
            recently created boards, so a truncated walk can return zero
            PIPELINE boards while the org has plenty — and this branch is what
            renders then. Without it the screen says "no pipelines" to a user
            who has them. */}
        {hasUnreachableBoards ? renderTruncationIndicator() : null}
        {renderSeeAllTrigger(onOpenSeeAllDrawer)}
      </Stack>
    ) : (
      // SE-6554: one uniform 8px rhythm across the whole row, matching the
      // design frame's chip spacing. The trailing triggers previously carried
      // an extra inset on top of the row gap, which read as a ragged double
      // step exactly where the design has none.
      <Stack direction="horizontal" className="h-10 items-center gap-2">
        {chips.map(chip => (
          <Pressable
            key={chip.id}
            testID={TEST_ID_PIPELINE_CHIPS_ROW.chipFor(chip.id)}
            accessibilityRole="button"
            accessibilityLabel={chip.name}
            accessibilityState={{ selected: chip.id === selectedBoardId }}
            onPress={chipHandlers[chip.id]}
            className={
              chip.id === selectedBoardId
                ? ACTIVE_CHIP_CLASS
                : INACTIVE_CHIP_CLASS
            }
          >
            <Text
              variant="body-bold"
              numberOfLines={1}
              className={
                chip.id === selectedBoardId
                  ? ACTIVE_CHIP_TEXT
                  : INACTIVE_CHIP_TEXT
              }
            >
              {chip.name}
            </Text>
          </Pressable>
        ))}

        {onCreatePipeline ? renderCreateTrigger(onCreatePipeline) : null}

        {hasUnreachableBoards ? renderTruncationIndicator() : null}

        {renderSeeAllTrigger(onOpenSeeAllDrawer)}
      </Stack>
    )}
  </Box>
);

PipelineChipsRowView.displayName = "PipelineChipsRowView";

export default memo(PipelineChipsRowView);
